import { Link } from "@tanstack/react-router";
import {
  count,
  decimal,
  moneyWithCents,
  type ReportColumn,
} from "../shared/ReportTable";
import type {
  SettlementSummaryMatter,
  SettlementSummaryTeam,
  SettlementSummaryWorkload,
} from "@/lib/data/requests/reports/reports-settlement-summary";

export const issueColumns: ReportColumn<SettlementSummaryTeam>[] = [
  { label: "Team", value: (r) => r.label, render: (r) => r.label },
  {
    label: "Accepted amount",
    numeric: true,
    value: (r) => (r.issueAmount === null ? null : Number(r.issueAmount)),
    render: (r) => moneyWithCents(r.issueAmount),
  },
  {
    label: "Matters",
    numeric: true,
    value: (r) => r.issueMatters,
    render: (r) => count(r.issueMatters),
  },
  {
    label: "Accepted offers",
    numeric: true,
    value: (r) => r.issueRecords,
    render: (r) => count(r.issueRecords),
  },
];
export const awaitingColumns: ReportColumn<SettlementSummaryTeam>[] = [
  { label: "Team", value: (r) => r.label, render: (r) => r.label },
  {
    label: "Accepted amount",
    numeric: true,
    value: (r) => (r.awaitingAmount === null ? null : Number(r.awaitingAmount)),
    render: (r) => moneyWithCents(r.awaitingAmount),
  },
  {
    label: "Matters",
    numeric: true,
    value: (r) => r.awaitingMatters,
    render: (r) => count(r.awaitingMatters),
  },
  {
    label: "Accepted records",
    numeric: true,
    value: (r) => r.awaitingRecords,
    render: (r) => count(r.awaitingRecords),
  },
];
export const workloadColumns: ReportColumn<SettlementSummaryWorkload>[] = [
  { label: "Assigned user", value: (r) => r.label, render: (r) => r.label },
  {
    label: "Open matters with liens",
    numeric: true,
    value: (r) => r.matters,
    render: (r) => count(r.matters),
  },
  {
    label: "Recorded liens",
    numeric: true,
    value: (r) => r.liens,
    render: (r) => count(r.liens),
  },
  {
    label: "Avg days since acceptance",
    numeric: true,
    value: (r) =>
      r.averageDaysSinceAcceptance === null
        ? null
        : Number(r.averageDaysSinceAcceptance),
    render: (r) => decimal(r.averageDaysSinceAcceptance),
  },
];
export const closedColumns: ReportColumn<SettlementSummaryMatter>[] = [
  {
    label: "Matter",
    value: (r) => r.matterName ?? r.matterId,
    render: (r) => (
      <Link
        className="text-primary hover:underline"
        to="/matters/$matterId"
        params={{ matterId: r.matterId }}
      >
        {r.matterName || r.matterId}
      </Link>
    ),
  },
  {
    label: "Team",
    value: (r) => r.teams.join(", ") || "Unassigned",
    render: (r) => r.teams.join(", ") || "Unassigned",
  },
  { label: "Closed", value: (r) => r.closedDate, render: (r) => r.closedDate },
  {
    label: "Current accepted amount",
    numeric: true,
    render: (r) => moneyWithCents(r.acceptedAmount),
  },
  { label: "Demands sent", numeric: true, render: (r) => count(r.demands) },
  {
    label: "Current accepted records",
    numeric: true,
    render: (r) => count(r.acceptedRecords),
  },
  {
    label: "Accepted / Demands",
    numeric: true,
    render: (r) =>
      r.acceptedPerDemandPercent === null
        ? "—"
        : `${decimal(r.acceptedPerDemandPercent)}%`,
  },
  {
    label: "Days demand to acceptance",
    numeric: true,
    render: (r) => decimal(r.daysDemandToAcceptance),
  },
  {
    label: "Days created to acceptance",
    numeric: true,
    render: (r) => decimal(r.daysToAcceptance),
  },
  {
    label: "Days created to closed",
    numeric: true,
    render: (r) => count(r.daysToClose),
  },
  {
    label: "Expected fees / 30 days",
    numeric: true,
    render: (r) => moneyWithCents(r.expectedFeesPer30Days),
  },
  {
    label: "Days acceptance to deposit",
    numeric: true,
    render: (r) => decimal(r.daysAcceptanceToDeposit),
  },
  {
    label: "Days deposit to closed",
    numeric: true,
    render: (r) => decimal(r.daysDepositToClose),
  },
  {
    label: "Expected fees",
    numeric: true,
    render: (r) => moneyWithCents(r.expectedFees),
  },
  {
    label: "Trust deposits",
    numeric: true,
    render: (r) => moneyWithCents(r.trustDeposits),
  },
  {
    label: "Expense cost",
    numeric: true,
    render: (r) => moneyWithCents(r.expenseCost),
  },
];
